const UNIFORM_NAMES = [
  'uRes',
  'uPhase',
  'uDrift',
  'uScale',
  'uBlur',
  'uWarp',
  'uSpread',
  'uSoft',
  'uLateral',
  'uGrain',
  'uGrainSize',
  'uPixelScale',
  'uSeed',
  'uBand1',
  'uBand2',
  'uBand3',
  'uBand4',
  'uBand5',
  'uHaze',
  'uWash',
  'uMist',
  'uInk',
  'uOlive',
  'uAcid',
]

function compileShader(gl, type, source) {
  const shader = gl.createShader(type)
  if (!shader) throw new Error('Failed to create shader')
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) || 'unknown error'
    gl.deleteShader(shader)
    throw new Error(`Shader compile failed: ${log}`)
  }
  return shader
}

function linkProgram(gl, vertSrc, fragSrc) {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vertSrc)
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fragSrc)
  const prog = gl.createProgram()
  if (!prog) throw new Error('Failed to create program')
  gl.attachShader(prog, vs)
  gl.attachShader(prog, fs)
  gl.linkProgram(prog)
  gl.deleteShader(vs)
  gl.deleteShader(fs)
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(prog) || 'unknown error'
    gl.deleteProgram(prog)
    throw new Error(`Program link failed: ${log}`)
  }
  return prog
}

/**
 * Pure WebGL2 renderer. Draw path is a pure function of (params, phase).
 */
export function createRenderer(canvas, vertSrc, fragSrc) {
  const glCtx = canvas.getContext('webgl2', {
    antialias: false,
    preserveDrawingBuffer: true,
    alpha: false,
    premultipliedAlpha: false,
    desynchronized: false,
  })

  if (!glCtx) {
    throw new Error('WebGL2 is not available')
  }

  const gl = glCtx

  let program = linkProgram(gl, vertSrc, fragSrc)
  let uniforms = {}

  const buf = gl.createBuffer()
  gl.bindBuffer(gl.ARRAY_BUFFER, buf)
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 3, -1, -1, 3]),
    gl.STATIC_DRAW,
  )

  function bindProgram(prog) {
    gl.useProgram(prog)
    uniforms = {}
    for (const name of UNIFORM_NAMES) {
      uniforms[name] = gl.getUniformLocation(prog, name)
    }
    const loc = gl.getAttribLocation(prog, 'aPos')
    gl.bindBuffer(gl.ARRAY_BUFFER, buf)
    gl.enableVertexAttribArray(loc)
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)
  }

  bindProgram(program)

  function setUniforms(params, phase) {
    const w = canvas.width
    const h = canvas.height
    gl.uniform2f(uniforms.uRes, w, h)
    gl.uniform1f(uniforms.uPhase, phase)
    gl.uniform1f(uniforms.uDrift, params.drift)
    gl.uniform1f(uniforms.uScale, params.scale)
    gl.uniform1f(uniforms.uBlur, params.blur)
    gl.uniform1f(uniforms.uWarp, params.warp)
    gl.uniform1f(uniforms.uSpread, params.spread)
    gl.uniform1f(uniforms.uSoft, params.soft)
    gl.uniform1f(uniforms.uLateral, params.lateral)
    gl.uniform1f(uniforms.uGrain, params.grain)
    gl.uniform1f(uniforms.uGrainSize, params.grainSize)
    gl.uniform1f(uniforms.uPixelScale, params.pixelScale)
    gl.uniform1f(uniforms.uSeed, params.seed)
    gl.uniform1f(uniforms.uBand1, params.band1)
    gl.uniform1f(uniforms.uBand2, params.band2)
    gl.uniform1f(uniforms.uBand3, params.band3)
    gl.uniform1f(uniforms.uBand4, params.band4)
    gl.uniform1f(uniforms.uBand5, params.band5)
    gl.uniform3f(uniforms.uHaze, params.haze[0], params.haze[1], params.haze[2])
    gl.uniform3f(uniforms.uWash, params.wash[0], params.wash[1], params.wash[2])
    gl.uniform3f(uniforms.uMist, params.mist[0], params.mist[1], params.mist[2])
    gl.uniform3f(uniforms.uInk, params.ink[0], params.ink[1], params.ink[2])
    gl.uniform3f(uniforms.uOlive, params.olive[0], params.olive[1], params.olive[2])
    gl.uniform3f(uniforms.uAcid, params.acid[0], params.acid[1], params.acid[2])
  }

  function renderFrame(frame) {
    const phase = ((frame.phase % 1) + 1) % 1
    gl.viewport(0, 0, canvas.width, canvas.height)
    setUniforms(frame.params, phase)
    gl.drawArrays(gl.TRIANGLES, 0, 3)
  }

  function destroy() {
    gl.deleteProgram(program)
    gl.deleteBuffer(buf)
  }

  return {
    renderFrame,
    destroy,
    canvas,
  }
}
